import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { cloneDeep, find, omit } from "lodash";

import {
  durationToHHmm,
  hasItems,
  getStorageKey,
  navigate,
  decToHHmm,
  setStorageItem,
  hhMMToDec,
} from "../../../../helpers/utils";

import { ReflectionProvider } from "../../reflection/ReflectionContext";
import AppMessages from "../../../../config/AppMessages";
import BottomButton from "../../common/BottomButton";
import EditRoutineFabButton from "./EditRoutineFabButton";
import TimePickerDialog from "../../common/TimePickerDialog";
import EditRoutineMedia from "./EditRoutineMedia";
import appStyles from "../../../appStyles";
import LastWeekHelper from "../../onboarding/last-week/LastWeekHelper";
import ReflectionList from "../../reflection/list/ReflectionList";
import EditRoutineElements from "./EditRoutineElements";
import ManaConfirmDialog from "../../common/dialog/ManaConfirmDialog";
import Header from "../../common/header/index";
import NoResultsFound from "../NoResultsFound";
import Toggle from "../../common/Toggle";
import AppTheme from "../../AppTheme";
import moment from "moment";
import RoutineConfirmDialog from "./RoutineConfirmDialog";
import appPages from "../../../../appPages";

export default class EditRoutinePage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.templateName = props.templateName;
    this.storageKey = getStorageKey(this.controller, "ROUTINE_HOURS");

    this.state = {
      showConfirm: false,
      showUndoConfirmation: false,
      template_data: [],
      hideActions: false,
      routine: {},
    };
  }

  componentDidMount = async () => {
    // When coming back from Customize Elements > Create Element
    /* if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } */

    this.refreshPage();
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { routine, applyDate } = this.getArgs() || {};

    if (!routine) {
      return navigate(this.controller, "rn:routines");
    }

    const isDailyRoutine = routine.time_interval != "week";
    this.MAX_HOURS = isDailyRoutine ? 24 : LastWeekHelper.WEEKLY_HOURS;

    this.setState(
      (state) => ({
        ...state,
        applyDate,
        routine: {
          ...routine,
          isDailyRoutine,
        },
      }),
      () => {
        this.initTemplateModel({
          templateHours: this._getRoutineHours(routine.hours),
          updateOld: false,
          autosave: false,
        });
      },
    );
  };

  _getRoutineHours = (hoursJson) => {
    if (!hoursJson) return [];

    // Messing with autoFit
    const hours = JSON.parse(hoursJson);
    hours.forEach((h) => (h.is_modified = false));

    return hours;
  };

  // ------------------------------------- MVP -------------------------------------
  initTemplateModel = (params) => {
    const {
      templateHours,
      showElements = false,
      updateOld = false,
      showAll = false,
      autosave = true,
    } = params;

    const { routine } = this.state;
    this.setState((state) => ({ ...state, isLoading: false }));

    //const templateModel = LastWeekHelper.getElementsHierarchy(this.controller, templateHours);
    const templateModel = LastWeekHelper.getTemplateDataModel(
      templateHours,
      this.controller.state?.elements,
      this.controller.state?.reflection_root,
      true,
    );

    const totalHours = this.getTotalHours(templateHours);

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        template_data: templateModel,
        elements: [...templateHours],
        old_elements: updateOld
          ? cloneDeep(templateHours)
          : this.state.old_elements,
        showElements,
        showAll,
        totalHours,
      }),
      () => {
        // Autosave
        if (autosave && routine?.id) {
          this.autoSaveHours();
        }
      },
    );
  };

  // ------------------------------------- MESSAGES -------------------------------------
  clearHoursError = () => {
    const { message } = this.state;

    if (message != null) {
      this.setState((state) => ({
        ...state,
        message: "",
      }));
    }
  };

  showHoursError = (totalHours) => {
    const {
      routine: { time_interval },
    } = this.state;

    let message =
      totalHours < this.MAX_HOURS && time_interval != "day"
        ? AppMessages.NotEnoughHoursRoutine
        : totalHours > this.MAX_HOURS
        ? AppMessages.TooManyHoursRoutine
        : "";

    message = message
      .replace("{TIME_INTERVAL}", time_interval)
      .replace("{TOTAL}", totalHours)
      .replace("{MAX}", this.MAX_HOURS)
      .replace("{AVAILABLE}", this.MAX_HOURS);

    this.setState((state) => ({
      ...state,
      message,
    }));

    return message;
  };

  // ------------------------------------- TIMEPICKER -------------------------------------
  showTimePicker = (selectedItem, day = {}, dayIndex, level = 0) => {
    if (selectedItem.item_type == "set" || selectedItem.is_uncategorized) {
      return;
    }

    const { hours, minutes } = day.hours
      ? durationToHHmm(day.hours)
      : { hours: 0, minutes: 0 };

    this.setState((state) => ({
      ...state,
      selectedItem: {
        ...selectedItem,
        isDaySelected:
          this.state.routine.time_interval === "day" || dayIndex == 1, // reversed order
        level,
        time: `${hours}:${minutes}`,
      },
    }));
  };

  closeTimePicker = () => {
    this.setState((state) => ({
      ...state,
      selectedItem: null,
    }));
  };

  updateEndTime = (selectedTime) => {
    const { routine, elements, selectedItem, totalHours } = this.state;

    let newDecTime = hhMMToDec(selectedTime);

    this.setState((state) => ({ ...state, message: "" }));

    let currentItem = find(
      elements,
      (el) => el.id_activity == selectedItem.item_id,
    );

    if (!currentItem || newDecTime == selectedItem.time) return;

    const oldHours =
      1 *
      (selectedItem.isDaySelected
        ? currentItem.hours
        : currentItem.total_hours);

    //const totalHours = this.getTotalHours(elements);
    const remaining = this.MAX_HOURS - (totalHours - oldHours);
    const newTotal = totalHours - oldHours + 1 * newDecTime;

    // Do not allow user to input over 24 hours. If user tries to enter more than 24hrs,
    // cap the number they are trying to edit to the max it can be to reach 24 and display Mari modal
    if (1 * newDecTime > remaining) {
      this.showHoursError(newTotal);

      // Create: When a user goes over 168hrs while creating a week routine,
      // allow the user to go over 168 hours but show modal and disable create routine button.
      // Only enable create routine button when the routine is 168 hours or less
      newDecTime = routine.id ? Math.max(remaining, 0) : newDecTime;
    } else {
      this.clearHoursError();
    }

    currentItem.is_modified = true;
    if (selectedItem.isDaySelected) {
      currentItem.hours = newDecTime;
      currentItem.total_hours = newDecTime * 7;
    } else {
      currentItem.total_hours = newDecTime;
      currentItem.hours =
        newDecTime > 0 ? newDecTime / LastWeekHelper.getDailyAdjustment() : 0;
    }

    this.setState((state) => ({
      ...state,
      hasModifiedHours: true,
    }));

    this.initTemplateModel({
      templateHours: [...elements],
      updateOld: false,
      autosave: true,
    });
  };

  getTotalHours = (templateHours = []) => {
    const { routine } = this.state;

    let totalHours = 0;
    let selectedElements = {};
    templateHours.forEach((th) => {
      if (
        !selectedElements[th.id_activity] &&
        selectedElements[th.id_activity + "_misc"] == null
      ) {
        //add elements only once and if they do not have a generic counterpart
        selectedElements[th.id_activity] = 1;
        totalHours += 1 * (routine?.isDailyRoutine ? th.hours : th.total_hours);
      }
    });

    return totalHours.toFixed(2);
  };

  // ------------------------------------- SAVE -------------------------------------
  isValid = () => {
    const { routine, totalHours } = this.state;

    return routine.name && (routine.id ? true : totalHours <= this.MAX_HOURS);
  };

  autoSaveHours = () => {
    if (!this.state.applyDate) {
      this.saveToDb(this.state.routine);
    }
  };

  updateRoutine = (routine) => {
    this.setState((state) => ({ ...state, routine }));

    // Autosave
    if (routine.id) {
      this.saveToDb(routine);
    }
  };

  createRoutine = () => {
    const { routine } = this.state;
    this.saveToDb(routine, () => {
      navigate(this.controller, "rn:routines");
    });
  };

  saveToDb = (routine, callback = undefined) => {
    const { template_data, totalHours } = this.state;

    if (!this.isValid()) {
      this.setState((state) => ({
        ...state,
        message: "You new routine needs a name!",
      }));
      return;
    }

    this.setState((state) => ({ ...state, isLoading: true }));

    const hours = LastWeekHelper.mapElementsToTemplateHours(template_data);
    const isPartial = totalHours * 1 + 1e-10 < 24;

    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "routine",
        ...omit(routine, ["isDuplicate", "isDailyRoutine"]),
        is_partial: isPartial,
        template_day: moment(routine.selectedDate).format("YYYY-MM-DD"),
        hours,
      },
      () => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          //message: "",
        }));
        if (callback) callback();
      },
    );
  };

  // ------------------------------------- FAB -------------------------------------
  fitHours = () => {
    const { elements } = this.state;

    LastWeekHelper.fitHours({
      uncategorizedActivity: {},
      activities: elements,
      timeInterval: this.controller.state.settings?.interval_duration / 60.0,
      fitToUncategorized: false,
      callback: this.initTemplateModel,
    });
  };

  onDeleteElement = (element) => {
    const { elements, tutorialStep } = this.state;

    const remainingActivities = elements.filter(
      (a) => a.id_activity != element.item_id,
    );

    this.initTemplateModel({
      templateHours: remainingActivities,
      updateOld: false,
      autosave: true,
    });

    if (tutorialStep == 5) this.loadTutorial(tutorialStep + 1);
  };

  goToElementsPage = () => {
    this.setState((state) => ({ ...state, showElements: true }));
  };

  goBack = (elements) => {
    const { showElements } = this.state;

    // Already on Routines page => go to feed
    if (!showElements) {
      this.controller.goBack();
    }

    // Coming from CustomiseElements page
    if (!elements) {
      this.setState((state) => ({ ...state, showElements: false }));
      return;
    }

    // Elements confirmed
    if (elements.elements) {
      this.setState((state) => ({ ...state, showElements: false }));
      const newElements = [];
      elements.elements.forEach((a) => {
        const act = JSON.parse(JSON.stringify(a));
        if (act.children != null && act.children.length > 0) {
          act.parents = [act.id];
          act.id += "_misc";
          act.id_activity += "_misc";
          act.name += " (Generic)";
          act.children = null;
        }
        newElements.push(act);
      });

      this.initTemplateModel({
        templateHours: newElements,
        autosave: true,
      });
    }
  };

  // ------------------------------------- UNDO -------------------------------------
  undoChanges = () => {
    this.setState((state) => ({ ...state, showUndoConfirmation: true }));
  };

  closeConfirmation = () => {
    this.setState((state) => ({ ...state, showUndoConfirmation: false }));
  };

  confirmUndoChanges = () => {
    this.setState((state) => ({ ...state, showUndoConfirmation: false }));
    this.initTemplateModel({
      templateHours: this.state.old_elements || [],
      autosave: false,
    });
  };

  // ------------------------------------- APPLY -------------------------------------
  toggleConfirmDialog = (isOpen) => {
    this.setState((state) => ({ ...state, showConfirmDialog: isOpen }));
  };

  onApplyPressed = () => {
    const { routine, hasModifiedHours } = this.state;

    this.setState((state) => ({
      ...state,
      message: AppMessages.RoutineApplyInProgress,
    }));

    this.applyRoutine(routine, (data) => {
      this.setState(
        (state) => ({
          ...state,
          routine: { ...routine, partial: data?.partial == "true" },
        }),
        () => {
          // Show confirmation
          if (hasModifiedHours) {
            this.toggleConfirmDialog(true);
          } else {
            this.goToTrackPage();
          }
        },
      );
    });
  };

  applyRoutine = (routine, callback) => {
    const { template_data } = this.state;

    const hours = LastWeekHelper.mapElementsToTemplateHours(template_data);

    this.controller.apiService
      .cardAction({
        action: "apply_routine",
        routine_id: routine.id,
        apply_to: routine.time_interval, // day or week
        startTime: this.state.applyDate,
        hours,
      })
      .then(callback);
  };

  saveRoutine = (routine) => {
    this.toggleConfirmDialog(false);

    this.saveToDb(routine, () => {
      this.goToTrackPage();
    });
  };

  goToTrackPage = () => {
    const { routine } = this.state;

    this.toggleConfirmDialog(null);
    if (routine.partial) {
      setStorageItem("overrideRoutine", routine);
    }

    let delay = 0;
    if (routine.partial) {
      setStorageItem("overrideRoutine", routine);
      delay = 500;
    }

    setTimeout(() => {
      this.setState((state) => ({ ...state, message: "" }));
      this.controller.getApiData(
        "timeslots",
        "",
        this.controller.navigate(appPages.trackHome),
      );
    }, delay);
  };

  // ------------------------------------- RENDER -------------------------------------

  render() {
    const {
      showElements,
      template_data,
      isLoading,
      routine,
      totalHours,
      selectedItem,
      showUndoConfirmation,
      message,
      mariAction,
      elements,
      applyDate,
      showConfirmDialog,
    } = this.state;

    const totalHoursHHmm = decToHHmm(totalHours);
    const minutesInterval =
      this.controller.state?.settings?.interval_duration || 1;

    const title =
      (routine.id ? "Edit " : "Create ") +
      (routine.isDailyRoutine ? "" : "week ") +
      "routine";

    return (
      <ReflectionProvider
        value={{
          controller: this.controller,
          pageType: "routine",
          isDailyRoutine: routine.isDailyRoutine,
          totalCount: template_data?.length,
          onDeleteElement: this.onDeleteElement,
          onHoursSelected: this.showTimePicker,
          onElementSelected: () => {},
          saveRoutine: this.updateRoutine,
        }}
      >
        {showElements ? (
          <View style={appStyles.container}>
            <EditRoutineElements
              controller={this.controller}
              currentElements={elements}
              goBack={this.goBack}
              showAll={this.state.showAll}
            />
          </View>
        ) : (
          <View
            style={[
              appStyles.container,
              { backgroundColor: AppTheme.lightGray },
            ]}
          >
            <Header
              title={title}
              hideAvatar
              goBack={this.goBack}
              isLoading={isLoading}
            >
              <Toggle
                selected={`Hours:(${totalHoursHHmm})`}
                //options={[`Hours:(${totalHoursHHmm})`, "Schedule"]}
                options={[`Hours:(${totalHoursHHmm})`]}
                onToggle={(value) => {}}
                style={{
                  backgroundColor: "#f3f2f1",
                  height: 24,
                  marginHorizontal: 16,
                }}
                selectedStyle={{
                  color: AppTheme.DEFAULT_COLOR,
                }}
              />
            </Header>

            <EditRoutineMedia routine={routine} readOnly={!!applyDate} />

            <ScrollView contentContainerStyle={appStyles.scrollViewContent}>
              {!hasItems(template_data) ? (
                <NoResultsFound
                  message={AppMessages.EditRoutineNoResultsMessage}
                  buttons={[
                    {
                      action: () => this.goToElementsPage(),
                      text: AppMessages.AddElementsButtonText,
                    },
                  ]}
                />
              ) : (
                <ReflectionList
                  reflections={template_data}
                  controller={this.controller}
                  reverse={false}
                  source={"routine"}
                />
              )}
            </ScrollView>

            <EditRoutineFabButton
              style={{ bottom: 80 }}
              fitHours={this.fitHours}
              goToElementsPage={this.goToElementsPage}
              undoChanges={this.undoChanges}
              weeklyHours={this.MAX_HOURS}
              mariOptions={{
                mariMessage: selectedItem ? "" : message,
                mariAction: selectedItem ? {} : mariAction,
              }}
            />

            {/*  Create or Apply */}
            {!routine?.id || applyDate ? (
              <View style={appStyles.footerOptions}>
                {applyDate ? (
                  <BottomButton
                    buttonText="Apply Routine"
                    onPress={this.onApplyPressed}
                  />
                ) : (
                  <BottomButton
                    buttonText="Create Routine"
                    onPress={this.createRoutine}
                    disabled={!this.isValid()}
                  />
                )}
              </View>
            ) : null}
          </View>
        )}

        {/* TimePicker popup */}
        {selectedItem ? (
          <TimePickerDialog
            isOpen
            pickDuration
            use24hFormat
            hourDigits={3}
            debounceDelay={500}
            value={selectedItem.time}
            minutesInterval={minutesInterval}
            onConfirm={this.updateEndTime}
            onClose={this.closeTimePicker}
          />
        ) : null}

        {/* Confirm Undo Dialog */}
        <ManaConfirmDialog
          isOpen={showUndoConfirmation}
          title="Are you sure?"
          description="Tapping confirm will revert all manual changes to hour counts to default values."
          onConfirm={this.confirmUndoChanges}
          onClose={this.closeConfirmation}
        />

        {/* Apply Confirm Dialog */}
        {showConfirmDialog ? (
          <RoutineConfirmDialog
            routine={routine}
            controller={this.controller}
            saveRoutine={this.saveRoutine}
            applyRoutine={this.goToTrackPage}
            close={() => this.toggleConfirmDialog(null)}
          />
        ) : null}
      </ReflectionProvider>
    );
  }
}
